interface AvatarProps {
  seed: string;
  /** Display name (or any string) to pick the fallback letter from. If
   *  omitted, uses seed. */
  name?: string | null;
  src?: string | null;
  size?: number;
  ringColor?: string;
  className?: string;
}

// YouTube-style muted palette. Deterministic pick via djb2 hash of seed so
// the same user always gets the same color across the app.
const PALETTE = [
  '#DA534D', // red
  '#E67E22', // orange
  '#D4A017', // mustard
  '#2E8B57', // sea green
  '#1F8FA6', // teal
  '#3B6EA5', // steel blue
  '#7D5FB2', // purple
  '#B04A80', // magenta
];

function hashIndex(s: string, mod: number): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h % mod;
}

function firstLetter(s: string | null | undefined): string {
  if (!s) return '?';
  // Grab the first code point to handle emoji / multi-byte characters.
  const ch = Array.from(s.trim())[0];
  return (ch ?? '?').toUpperCase();
}

export function Avatar({
  seed,
  name,
  src,
  size = 36,
  ringColor,
  className = '',
}: AvatarProps) {
  const outer = ringColor
    ? { padding: 2, background: ringColor }
    : undefined;

  const letter = firstLetter(name ?? seed);
  const color = PALETTE[hashIndex(seed || letter, PALETTE.length)];
  // Font size roughly tracks avatar diameter.
  const fontPx = Math.max(10, Math.round(size * 0.45));

  return (
    <div
      className={`shrink-0 rounded-full ${className}`}
      style={{ width: size, height: size, ...outer }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="size-full rounded-full object-cover"
        />
      ) : (
        <div
          aria-hidden
          className="flex size-full items-center justify-center rounded-full font-display font-semibold leading-none text-white select-none"
          style={{ backgroundColor: color, fontSize: fontPx }}
        >
          {letter}
        </div>
      )}
    </div>
  );
}
